/* Copyright (c) Trimble Inc. All rights reserved. */
import React, { useEffect, useRef } from "react";
import { Box } from "@material-ui/core";
import { useHistory, useLocation, useParams } from "react-router";
import { useProductDetailsController } from "../components/product-details/ProductDetails.controller";
import { ProductDetailsViewer } from "../components/product-details/ProductDetails.viewer";
import { SubscriberPage } from "pages/home/subscriptions/pages/SubscriptionPage";
import { Breadcrumbs } from "modus/components/generic-page-title/Breadcrumbs";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import ApplicationTypeService from "../services/ApplicationTypeService";
import { SET_APPLICATION_TYPES } from "store/actions/actions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/types";
import Alert from "legacy-components/alert/Alert";
import AlertService from "legacy-components/alert/AlertService";
import {
  PRODUCT_SUMMARY_CREATED_ALERT_ID,
  PRODUCT_SUMMARY_VISIBILITY_ALERT_ID,
  formatProductVisibilityLabel,
} from "./productSummaryConstants";

type ProductCreatedLocationState = {
  productJustCreated?: boolean;
  createdProductName?: string;
};

type ProductVisibilityUpdatedLocationState = {
  productVisibilityUpdated?: boolean;
  /** Display name for the alert message */
  visibilityUpdateProductName?: string;
  /** Normalized visibility value (e.g. team, public) */
  visibilityUpdateNewValue?: string;
};

export const ProductSummary: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { response, subscribe } = useProductDetailsController();
  const location = useLocation();
  const { productId } = useParams<any>();
  const applicationTypesList = useSelector((state: RootState) => state.applicationTypesList);
  const creationAlertShownForKey = useRef<string | null>(null);
  const visibilityAlertShownForKey = useRef<string | null>(null);

  const [appTypesState, { subscribe: fetchApplicationTypes }] = useAsyncAction(() =>
    ApplicationTypeService.getApplicationTypes()
  );

  useEffect(() => {
    if (applicationTypesList != null && applicationTypesList.length > 0) return;
    fetchApplicationTypes();
  }, [applicationTypesList, fetchApplicationTypes]);

  useEffect(() => {
    if (!appTypesState) return;
    if (appTypesState.loaded && appTypesState.value !== undefined) {
      const data = appTypesState.value;
      dispatch({
        type: SET_APPLICATION_TYPES,
        payload: Array.isArray(data) ? data : null
      });
    } else if (appTypesState.error) {
      dispatch({ type: SET_APPLICATION_TYPES, payload: null });
    }
  }, [appTypesState, dispatch]);

  useEffect(() => {
    const state = (location.state || {}) as ProductCreatedLocationState;
    if (!state.productJustCreated || !state.createdProductName?.trim()) {
      creationAlertShownForKey.current = null;
      return;
    }
    const dedupeKey = `${location.key}|${state.createdProductName}`;
    if (creationAlertShownForKey.current === dedupeKey) {
      return;
    }
    creationAlertShownForKey.current = dedupeKey;

    AlertService.success({
      id: PRODUCT_SUMMARY_CREATED_ALERT_ID,
      message: `Product ${state.createdProductName.trim()} created.`,
      isAutoHide: true,
      autoHideDurationMs: 5000,
      isShowClose: true,
    });

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: {},
    });
  }, [location, history]);

  useEffect(() => {
    const state = (location.state || {}) as ProductVisibilityUpdatedLocationState;
    if (
      !state.productVisibilityUpdated ||
      !state.visibilityUpdateProductName?.trim() ||
      !state.visibilityUpdateNewValue
    ) {
      visibilityAlertShownForKey.current = null;
      return;
    }
    const dedupeKey = `${location.key}|${state.visibilityUpdateProductName}|${state.visibilityUpdateNewValue}`;
    if (visibilityAlertShownForKey.current === dedupeKey) {
      return;
    }
    visibilityAlertShownForKey.current = dedupeKey;

    const label = formatProductVisibilityLabel(state.visibilityUpdateNewValue);
    AlertService.success({
      id: PRODUCT_SUMMARY_VISIBILITY_ALERT_ID,
      message: `${state.visibilityUpdateProductName.trim()} visibility updated to ${label}.`,
      isAutoHide: true,
      autoHideDurationMs: 5000,
      isShowClose: true,
    });

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: {},
    });
  }, [location, history]);

  return (
    <Box m={2}>
      <Alert id={PRODUCT_SUMMARY_CREATED_ALERT_ID} />
      <Alert id={PRODUCT_SUMMARY_VISIBILITY_ALERT_ID} />
      <Breadcrumbs
        links={[{ text: "Products", href: "/home/consumer/products" }]}
        pageTitle={response?.value?.data?.name}
      />
      {location.pathname ===
      `/home/consumer/products/${productId}/subscribe` ? (
        <SubscriberPage response={response} loadProduct={subscribe} />
      ) : (
        <ProductDetailsViewer response={response} subscribe={subscribe} />
      )}
    </Box>
  );
};

export default ProductSummary;
